import React, { useEffect, useState, useCallback, memo, useMemo, useContext } from "react";
import AppContext from 'context/Context';
import { Card, Col, Row, Tabs, Tab } from "react-bootstrap";
import WidgetRow from "./WidgetRow";
import StillageTable from "../stillage-table/StillageTable";
import StillageMap from "../stillage-map/StillageMap";
import { getStillageTableDetails, stillageTypes, updateStillageType, updateLostStillage } from "../../../../services/stillageDashboard";
import { useSelector, useDispatch } from 'react-redux';
import WidgetModal from '../widgets/WidgetModal';
import { closeModal } from 'slices/stillageWidgetModalSlice';
import { toast } from "react-toastify";
import SearchRow from "./SearchRow";
import AgedStillages from "./AgedStillages";
import dayjs from "dayjs";
import CollectionWidget from "../widgets/CollectionWidget";

const StillageDashboard = () => {
    const { setConfig } = useContext(AppContext);
    const [editingTypeStillageNo, setEditingTypeStillageNo] = useState(null);
    const [key, setKey] = useState('management');
    const [searchFilter, setSearchFilter] = useState({
        requiredDateFrom: dayjs().format('YYYY-MM-DD'),
        requiredDateTo: dayjs().format('YYYY-MM-DD'),
        stillageType: null,
        customer: null,
        query: null,
        stillageNo: null
    });
    const defaultData = useMemo(() => ({
        tableData: [],
        trackerData: [],
        mapData: []
    }), []);

    const modal = useSelector(state => state.stillageWidgetModal);
    const dispatch = useDispatch();

    const height = 550;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(defaultData);

    const searchData = (mounted, filters) => {
        setLoading(true);
        setData(defaultData);
        getStillageTableDetails(filters)
            .then(res => {
                if (!mounted) return;
                if (res.length > 0) {
                    const trackers = res.filter(x => (x.lat && x.lat > 0.0) || (x.long && x.long > 0.0));
                    setData({
                        tableData: res,
                        trackerData: trackers,
                        mapData: trackers,
                    });
                }
            })
            .catch((er) => {
                if (mounted)
                    setData(defaultData);
                toast.error('Something went wrong!', er)
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        let mounted = true;
        // Collapse nav on page load
        const classList = document.getElementsByTagName('html')[0].classList;
        if (!classList.contains('navbar-vertical-collapsed')) {
            document.getElementsByTagName('html')[0].classList.add('navbar-vertical-collapsed');
            setConfig('isNavbarVerticalCollapsed', true);
        }

        searchData(mounted, searchFilter);
        return () => mounted = false;
    }, []);

    // Called when the user picks a new type from react-select
    const handleChangeType = useCallback(async (stillageNo, newTypeValue) => {
        const newType = stillageTypes[newTypeValue];
        if (!newType) {
            toast.error('Invalid stillage type selected');
            return;
        }

        updateStillageType(stillageNo, newTypeValue)
            .then(success => {
                if (success) {
                    toast.success(`Stillage: ${stillageNo} updated successfully to: ${newType.text}`);

                    setData(prev => {
                        return {
                            ...prev,
                            tableData: prev.tableData?.map(row => {
                                if (row.stillageNo !== stillageNo)
                                    return row;

                                return {
                                    ...row,
                                    stillageTypeName: newTypeValue
                                };
                            })
                        }
                    });
                }
                else
                    toast.error(`Unable to update stillage ${stillageNo}! Please try again.`);
            })
            .catch(err => toast.error(`Something went wrong! ${err.message}`));

        // Close the dropdown after selecting
        setEditingTypeStillageNo(null);
    }, []);

    // Called when 
    const handleFBStillageUpdate = (comment, isLost, stillageId, stillageNo, originalComment, loading) => {
        loading(true);
        if (!comment) {
            toast.error('Please enter a comment before saving!');
            loading(false);
            return;
        }

        const json = { comment, isLost, stillageId, stillageNo, originalComment };
        updateLostStillage(json)
            .then(res => {
                console.log(res)
                if (res) {
                    toast.success('Stillage Status updated successfully!');

                    setData(prev => {
                        return {
                            ...prev,
                            tableData: prev.tableData?.map(row => {
                                if (row.stillageId !== stillageId)
                                    return row;

                                return {
                                    ...row,
                                    stillageComment: comment,
                                    lostStillage: isLost
                                };
                            })
                        }
                    });
                } else
                    toast.error('Unable to update stillage status!')
            })
            .catch(err => toast.error('Someting went wrong!', err))
            .finally(() => loading(false));
    }

    return (
        <div>
            <SearchRow filters={searchFilter} setFilters={setSearchFilter} loading={loading} search={searchData} />
            <WidgetRow data={data} loading={loading} />
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="p-4 shadow border-0" style={{ height, overflow: 'hidden' }}>
                        <Card.Title className="text-700 mb-2">Locations</Card.Title>
                        <StillageMap stillages={data?.mapData} />
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="p-4 shadow border-0" style={{ height, overflow: 'auto' }}>
                        <Card.Title className="text-700 mb-2">Forecast <small>(Current Week)</small></Card.Title>
                        <CollectionWidget />
                    </Card>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col sm={12}>
                    <Card className="p-4 shadow border-0" style={{ height, overflow: 'hidden' }}>
                        <Tabs
                            activeKey={key}
                            onSelect={(k) => setKey(k)}>
                            <Tab eventKey="management" title="Stillage Management" className="mb-1">
                                <StillageTable
                                    data={data?.tableData}
                                    loading={loading}
                                    editingTypeStillageNo={editingTypeStillageNo}
                                    setEditingTypeStillageNo={setEditingTypeStillageNo}
                                    onChangeType={handleChangeType}
                                    onUpdateStillage={handleFBStillageUpdate}
                                />
                            </Tab>
                            <Tab eventKey="aged" title="Aged Stillages" className="mb-1">
                                <AgedStillages data={data?.tableData || []} loading={loading} />
                            </Tab>
                        </Tabs>
                    </Card>
                </Col>
            </Row>
            <WidgetModal
                title={modal.title}
                showModal={modal.show}
                setShowModal={val => { if (!val) dispatch(closeModal()); }}
                modalData={modal.data}
                loading={modal.loading}
            />
        </div>
    );
}

export default memo(StillageDashboard);